<template>
  <button
    v-if="showNativeCta && products.length"
    class="
      shoppable-hotspot__cta
      shoppable-hotspot-cta
    "
    type="button"
    @click.prevent="toggleProducts(true)"
  >
    <icon-cart />

    <span
      class="
        shoppable-hotspot__label
        shoppable-hotspot-cta__label
        text-body-2xs-regular
      "
      v-text="$string('lookbook.shop_the_look')"
    />
  </button>

  <transition name="fade">
    <div
      v-if="showBackdrop && products.length && productsVisible"
      class="shoppable-hotspot__backdrop"
    />
  </transition>

  <transition name="fade">
    <div
      v-if="products.length && productsVisible"
      class="shoppable-hotspot"
      :class="{ 'limited-width': limitedWidth }"
    >
      <div class="shoppable-hotspot__buttons-wrapper">
        <button
          v-if="productSelected"
          class="shoppable-hotspot__close-atc"
          type="button"
          show-icon
          @click="closeAtc"
        >
          <icon-arrow-left />

          <span
            class="
              shoppable-hotspot__close-atc-label
              text-body-2xs-regular
            "
            v-text="
              showBackInStock
                ? $string('lookbook.back_to_look')
                : $string('lookbook.return_to_look')
            "
          />
        </button>

        <button
          v-else
          class="shoppable-hotspot__products-close"
          type="button"
          @click="toggleProducts(false)"
        >
          <icon-close />
        </button>
      </div>

      <div class="shoppable-hotspot__carousel-content-wrapper">
        <div
          class="shoppable-hotspot__carousel-wrapper"
        >
          <template v-for="(product, productIndex) of products">
            <div
              v-if="viewedProductIndex === -1 || viewedProductIndex === productIndex"
              :key="productIndex"
              class="shoppable-hotspot__product-wrapper"
            >
              <product-card
                :product="product"
                card-layout="small"
                :show-wishlist="false"
                :enable-flat-image="false"
                enable-variant-image
                :show-badges="false"
                :show-swatches="false"
                :load-secondary-image="false"
                :show-product-content="false"
                image-background-color="--color-brand-light-gray"
                :show-quick-add="!productSelected"
                enable-image-carousel
                :carousel-limit="4"
                custom-atc
                :product-card-location="productCardLocation"
                @custom-quick-add="handleAddToCart(productIndex)"
              />
            </div>
          </template>

          <div
            v-if="productSelected"
            class="shoppable-hotspot__product-details"
            :class="{
              'bis-active': showBackInStock
            }"
          >
            <quick-add-to-cart
              v-if="productsVisible && viewedProduct"
              size-guide-position="top"
              :number-of-sizes-per-row="7"
              show-confirmation-button
              show-view-product-button
              :product="viewedProduct"
              :show-back-in-stock="showBackInStock"
              show-product-details
              is-stl-type
              :product-card-location="productCardLocation"
              :bis-settings="{
                showCloseButton: false,
                namespace: `lookbook-${viewedProduct.handle}`,
                marketingText: $string('product.back_in_stock.marketing_opt_in')
              }"
              @bis-active="handleBackInStockModal"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>

/**
 * Vue: Shoppable Hotspot (shoppable-hotspot)
 * -----------------------------------------------------------------------------
 * Shoppable Hotspot section with image and video options.
 *
 * @param {Array} products - Section products.
 * @param {Boolean} active - Is section active.
 * @param {Boolean} showBackdrop - Should show backdrop.
 * @param {Boolean} showNativeCta - Should show built-in cta.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import ProductCard from '~global/product-card/product-card'
import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconClose from '~icons/general/close.svg'
import IconCart from '~icons/general/cart.svg'
export default {
  name: 'ShoppableHotspot',
  components: {
    IconArrowLeft,
    IconClose,
    IconCart,
    ProductCard,
    QuickAddToCart: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.quick-add-to-cart' */
        '~async/quick-add-to-cart/quick-add-to-cart'
      ),
    }),
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    productCardLocation: {
      type: Object,
      default: () => ({
        key: 'sectionHotspot',
      }),
    },
    active: {
      type: Boolean,
      default: false,
    },
    showBackdrop: {
      type: Boolean,
      default: false,
    },
    showNativeCta: {
      type: Boolean,
      default: false,
    },
    limitedWidth: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['closed'],

  data() {
    return {
      productsVisible: false,
      viewedProductIndex: -1,
      productSelected: false,
      viewedProduct: {},
      showBackInStock: false,
      productGridModalActive: false,
      blockIndex: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      activeOverlay: (state) => state.overlays.active[0],
      lastAddedProductId: (state) => state.quickAddToCart.lastAddedProductId,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      return this.$string('product.cta.add_to_cart')
    },
  },

  watch: {
    active() {
      this.toggleProducts(this.active)
    },

    lastAddedProductId() {
      if (this.lastAddedProductId) {
        this.closeAtcWithId()
      }
    },

    activeOverlay(activeOverlay) {
      if (activeOverlay?.namespace !== 'lookbook-product-list-modal') {
        this.toggleProducts(false)
      }
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setStlProducts: 'shopTheLook/setProducts',
    }),

    /**
     * Show/switch/hide product list
     */
    toggleProducts(show) {
      if (this.isMobile && show) {
        this.showOverlay('lookbook-product-list-modal')
        return
      }

      if (show) {
        this.productsVisible = true
        return
      }

      this.productsVisible = false
      this.$emit('closed')
      this.closeAtc()
    },

    /**
      * Open product list overlay.
      */
    showOverlay(component) {
      this.setStlProducts(this.products)
      this.openOverlay({
        ignoreDismissed: true,
        namespace: component,
      })
    },

    /**
     * Checks variant ids of the products from the each block and resets
     * the block where id was found
     */
    closeAtcWithId() {
      if (this.products.some((product) => {
        const variantIds = product.variants.map((variant) => variant.id)
        return variantIds.includes(Number(this.lastAddedProductId))
      })) {
        this.closeAtc()
      }
    },

    /**
     * Close ATC and show products only
     */
    closeAtc() {
      this.viewedProductIndex = -1
      this.productSelected = false
      this.viewedProduct = {}
      if (this.showBackInStock) {
        this.showBackInStock = false
      }
    },

    /**
     * Handle add to cart.
     */
    handleAddToCart(productsIndex) {
      this.viewedProduct = this.products[productsIndex]
      this.productSelected = true
      this.viewedProductIndex = productsIndex
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },
  },
}
</script>

<style lang="scss">
@import './shoppable-hotspot';
</style>
